import { Currency, CurrencyAmount, Token } from '@kodiak-finance/sdk-core'
import { AutoColumn } from 'components/Column'
import FiatValue from 'components/FiatValue'
import { RowBetween } from 'components/Row'
import { MouseoverTooltip } from 'components/Tooltip'
import { useTokens } from 'hooks/Tokens'
import { useApiTokenValues } from 'hooks/useTokenPrice'
import { useMemo } from 'react'
import { useTheme } from 'styled-components'
import { ThemedText } from 'theme'
import { KodiakFarm } from 'types/farms'

import * as S from '../Styleds'

// Define a unified stake interface that works for both farm and vault
type StakeInterface = {
  kekId?: string
  tokenId?: string
  id?: string
  liquidity: CurrencyAmount<Token> | undefined
  endingTimestamp?: Date | number
  lockMultiplier?: number
}

type Stakes = StakeInterface[] | null | undefined

type IslandPositionCardProps = {
  totalFarmLiquidityUSD?: number
  totalLiquidity?: CurrencyAmount<Token>
  stakingToken?: Token
  stakes: Stakes
  totalBalance?: CurrencyAmount<Token>
  farm?: KodiakFarm | null
  stakingType: 'farm' | 'vault' | 'none'
}

const useCalculatePositionValue = (stakes: Stakes) => {
  return useMemo(() => {
    if (!stakes || stakes.length === 0) return undefined

    return stakes.reduce((acc, stake) => {
      // if stake.liquidity is undefined, skip it
      if (!stake?.liquidity) return acc
      // if no accumulator yet, start with this stake's liquidity
      if (!acc) return stake.liquidity
      // add this stake's liquidity to accumulator
      return acc.add(stake.liquidity)
    }, undefined as CurrencyAmount<Token> | undefined)
  }, [stakes])
}

const formatMultiplier = (multiplier: number) => `${(multiplier / 1e18).toFixed(2)}x` // Assuming multiplier is in 18 decimals

const formatDuration = (duration: number) => `${Math.floor(Number(duration) / 86400)} days` // Convert seconds to days

const formatRewardTokens = (rewardTokens: Token[]) =>
  rewardTokens && rewardTokens.length > 0 ? rewardTokens.map((token) => token?.symbol).join(', ') : 'tokens'

const Position = ({
  name,
  balance,
  farmBalance,
  stakingToken,
  tooltip = true,
}: {
  name: string
  balance?: CurrencyAmount<Currency>
  farmBalance?: CurrencyAmount<Currency>
  stakingToken?: Token
  tooltip?: boolean
}) => {
  const theme = useTheme()
  const { values } = useApiTokenValues([balance as CurrencyAmount<Token>, farmBalance as CurrencyAmount<Token>])

  const balanceUSD = values?.[0]?.toExact()
  const farmBalanceUSD = values?.[1]?.toExact()

  const totalAmount = useMemo(() => {
    if (!balance && !farmBalance) return undefined
    if (!balance) return farmBalance
    if (!farmBalance) return balance

    // Ensure they have the same currency before adding
    if (balance.currency.equals(farmBalance.currency)) {
      return balance.add(farmBalance)
    }

    // If currencies don't match, just return the farm balance
    return farmBalance
  }, [balance, farmBalance])

  return (
    <AutoColumn>
      <ThemedText.DeprecatedBody fontSize="14px" fontWeight="400" lineHeight="14px" color={theme.textPrimary}>
        {name}
      </ThemedText.DeprecatedBody>

      <MouseoverTooltip
        disableHover={!tooltip}
        text={
          <>
            Wallet: {balance?.toSignificant(6) || '0.00'}
            <br />
            {name === 'Locked' || name === 'Staked' ? name : 'Staked'}: {farmBalance?.toSignificant(6) || '0.00'}
          </>
        }
      >
        <ThemedText.DeprecatedBody fontSize="20px" fontWeight="400" lineHeight="28px" color={theme.textPrimary}>
          <FiatValue value={[balanceUSD, farmBalanceUSD]} />
        </ThemedText.DeprecatedBody>
        <ThemedText.DeprecatedBody fontSize="12px" fontWeight="400" lineHeight="20px" color={theme.textSecondary}>
          {totalAmount?.toSignificant(6) || '0.00'} {stakingToken?.symbol}
        </ThemedText.DeprecatedBody>
      </MouseoverTooltip>
    </AutoColumn>
  )
}

export default function IslandPositionCard({
  totalFarmLiquidityUSD,
  totalLiquidity,
  totalBalance,
  stakingToken,
  stakes,
  farm,
  stakingType,
}: IslandPositionCardProps) {
  const theme = useTheme()

  // Process stakes based on staking type
  const { availableStakes, lockedStakes } = useMemo(() => {
    if (!stakes || stakes.length === 0) {
      return { availableStakes: [], lockedStakes: [] }
    }

    // Filter to non-empty stakes
    const filteredStakes = stakes.filter((item) => item?.liquidity?.greaterThan('0'))
    const now = Math.floor(Date.now() / 1000)

    // For vault, all positions are considered "staked" rather than locked/unlocked
    if (stakingType === 'vault') {
      return {
        availableStakes: [],
        lockedStakes: filteredStakes,
      }
    }

    // For farms, differentiate between locked and available stakes
    return {
      availableStakes: filteredStakes.filter((stake) => {
        // Check the endingTimestamp and convert to seconds if it's a Date object
        const timestamp =
          typeof stake.endingTimestamp === 'object'
            ? Math.floor(stake.endingTimestamp.getTime() / 1000)
            : Number(stake.endingTimestamp)
        return timestamp < now
      }),
      lockedStakes: filteredStakes.filter((stake) => {
        const timestamp =
          typeof stake.endingTimestamp === 'object'
            ? Math.floor(stake.endingTimestamp.getTime() / 1000)
            : Number(stake.endingTimestamp)
        return timestamp >= now
      }),
    }
  }, [stakes, stakingType])

  const availableBalance = useCalculatePositionValue(availableStakes)
  const lockedBalance = useCalculatePositionValue(lockedStakes)

  // Get reward tokens when we have a farm
  const rewardTokensAddresses = farm?.rewardTokens?.map((token) => token.id) || []
  const rewardTokens = useTokens(rewardTokensAddresses)

  // Helper to get the correct label based on staking type
  const getPositionLabel = (defaultLabel: string) => {
    if (stakingType === 'vault' && defaultLabel === 'Locked') {
      return 'Staked'
    }
    return defaultLabel
  }

  return (
    <S.IslandPositionCard>
      <S.IslandPositionDetails>
        <RowBetween>
          {/* Total Position (Wallet + All Staked) */}
          <Position
            name="My Deposits"
            balance={totalBalance}
            farmBalance={totalLiquidity}
            stakingToken={stakingToken}
          />

          {/* Available (Wallet + Unlocked Stakes) */}
          {availableStakes.length > 0 && (
            <Position
              name="Available"
              balance={totalBalance}
              farmBalance={availableBalance}
              stakingToken={stakingToken}
            />
          )}

          {/* Locked Stakes Only */}
          {lockedStakes.length > 0 && (
            <Position
              name={getPositionLabel('Locked')}
              farmBalance={lockedBalance}
              stakingToken={stakingToken}
              tooltip={false}
            />
          )}
        </RowBetween>
      </S.IslandPositionDetails>

      {/* Farm-specific information */}
      {stakingType === 'farm' && farm && (
        <S.InnerIslandCardFlex>
          <ThemedText.DeprecatedBody fontSize="12px" fontWeight="400" lineHeight="20px" color={theme.textSecondary}>
            {farm.lockMaxMultiplier && farm.lockTimeMax
              ? `Earn up to ${formatMultiplier(
                  Number(farm.lockMaxMultiplier)
                )} multiplier by locking for ${formatDuration(
                  Number(farm.lockTimeMax)
                )}. Earn rewards in ${formatRewardTokens(rewardTokens)}.`
              : `Earn rewards in ${formatRewardTokens(rewardTokens)}.`}
          </ThemedText.DeprecatedBody>
        </S.InnerIslandCardFlex>
      )}

      {/* Vault-specific information */}
      {stakingType === 'vault' && (
        <S.InnerIslandCardFlex>
          <ThemedText.DeprecatedBody fontSize="12px" fontWeight="400" lineHeight="20px" color={theme.textSecondary}>
            Earn {stakingToken?.symbol} rewards by staking your tokens in the reward vault.
          </ThemedText.DeprecatedBody>
        </S.InnerIslandCardFlex>
      )}
    </S.IslandPositionCard>
  )
}
